import type { TrustedDeviceChallenge, TrustedDeviceProof } from "../types";
import {
  BrowserTrustedDeviceError,
  toStorageFailure,
} from "./browser-trusted-device-error.ts";

/**
 * Lower-assurance installation record: an ECDSA key kept as an exportable
 * JWK in `localStorage` because this browser cannot persist `CryptoKey`
 * objects in IndexedDB. A script running on the page could copy it, which is
 * why the backend labels such devices `webcryptoExtractable`.
 */
type StoredExtractableInstallation = {
  version: 1;
  installationId: string;
  privateKey: JsonWebKey;
  publicKey: string;
};

/** localStorage key of the lower-assurance (extractable JWK) installation. */
export const EXTRACTABLE_INSTALLATION_STORAGE_KEY =
  "feather-trusted-device-extractable";

const ecdsaParameters = { name: "ECDSA", namedCurve: "P-256" } as const;

/**
 * Options for {@link createExtractableTrustedDeviceProof}.
 *
 * @example
 * await createExtractableTrustedDeviceProof(requestChallenge, { signal });
 */
export type ExtractableTrustedDeviceProofOptions = {
  signal?: AbortSignal;
  storage?: Storage;
};

function resolveStorage(storage?: Storage): Storage | undefined {
  if (storage) return storage;

  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    const error = new Error("Trusted-device preparation was abandoned");

    error.name = "AbortError";

    throw error;
  }
}

function isStoredExtractableInstallation(
  value: unknown,
): value is StoredExtractableInstallation {
  if (!value || typeof value !== "object") return false;

  const record = value as Partial<StoredExtractableInstallation>;

  return (
    record.version === 1 &&
    typeof record.installationId === "string" &&
    record.installationId.length >= 16 &&
    typeof record.publicKey === "string" &&
    record.publicKey.length > 0 &&
    Boolean(record.privateKey) &&
    typeof record.privateKey === "object"
  );
}

/**
 * Read the stored lower-assurance installation, if this browser has one.
 *
 * @example
 * const existing = readExtractableInstallation();
 */
export function readExtractableInstallation(
  storage?: Storage,
): StoredExtractableInstallation | undefined {
  const target = resolveStorage(storage);

  if (!target) return undefined;

  try {
    const raw = target.getItem(EXTRACTABLE_INSTALLATION_STORAGE_KEY);

    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as unknown;

    return isStoredExtractableInstallation(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether this browser already enrolled through the extractable fallback.
 *
 * @example
 * if (hasExtractableTrustedDeviceInstallation()) skipStrongEnrollment();
 */
export function hasExtractableTrustedDeviceInstallation(
  storage?: Storage,
): boolean {
  return readExtractableInstallation(storage) !== undefined;
}

function bytesToBase64(bytes: ArrayBuffer) {
  let binary = "";

  for (const byte of new Uint8Array(bytes)) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
}

function createInstallationId() {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return Array.from(crypto.getRandomValues(new Uint8Array(24)))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function createExtractableInstallation(
  storage: Storage,
): Promise<StoredExtractableInstallation> {
  let installation: StoredExtractableInstallation;

  try {
    const keyPair = await crypto.subtle.generateKey(ecdsaParameters, true, [
      "sign",
      "verify",
    ]);
    const [privateKey, publicKey] = await Promise.all([
      crypto.subtle.exportKey("jwk", keyPair.privateKey),
      crypto.subtle.exportKey("spki", keyPair.publicKey),
    ]);

    installation = {
      version: 1,
      installationId: createInstallationId(),
      privateKey,
      publicKey: bytesToBase64(publicKey),
    };
  } catch (error) {
    throw new BrowserTrustedDeviceError(
      "keyUnsupported",
      "Extractable ECDSA P-256 key generation is unavailable",
      { cause: error },
    );
  }

  const serialized = JSON.stringify(installation);

  try {
    storage.setItem(EXTRACTABLE_INSTALLATION_STORAGE_KEY, serialized);
  } catch (error) {
    throw toStorageFailure(error, "Trusted-device key could not be saved");
  }

  // Some webviews accept the write but drop it; a key that does not persist
  // would enroll a new device on every login, so refuse to use it.
  if (storage.getItem(EXTRACTABLE_INSTALLATION_STORAGE_KEY) !== serialized) {
    throw new BrowserTrustedDeviceError(
      "storageUnavailable",
      "Trusted-device key was not persisted by the browser",
    );
  }

  return installation;
}

/**
 * Create an installation proof with a key kept as an exportable JWK.
 *
 * @remarks
 * Second rung of the login ladder, used only when the browser has WebCrypto
 * but cannot keep a non-extractable `CryptoKey` in IndexedDB. The proof is
 * still a one-time challenge signature, so replay protection is unchanged;
 * only the at-rest protection of the key is weaker.
 *
 * @example
 * const proof = await createExtractableTrustedDeviceProof(requestChallenge);
 */
export async function createExtractableTrustedDeviceProof(
  requestChallenge: (installationId: string) => Promise<TrustedDeviceChallenge>,
  options: ExtractableTrustedDeviceProofOptions = {},
): Promise<TrustedDeviceProof> {
  const { signal } = options;
  const storage = resolveStorage(options.storage);

  throwIfAborted(signal);

  if (!storage) {
    throw new BrowserTrustedDeviceError(
      "storageUnavailable",
      "localStorage is unavailable for the trusted-device key",
    );
  }

  const installation =
    readExtractableInstallation(storage) ||
    (await createExtractableInstallation(storage));

  throwIfAborted(signal);

  let privateKey: CryptoKey;

  try {
    privateKey = await crypto.subtle.importKey(
      "jwk",
      installation.privateKey,
      ecdsaParameters,
      false,
      ["sign"],
    );
  } catch (error) {
    throw new BrowserTrustedDeviceError(
      "signingFailed",
      "The stored extractable key could not be imported",
      { cause: error },
    );
  }

  const challenge = await requestChallenge(installation.installationId);

  throwIfAborted(signal);

  let signature: ArrayBuffer;

  try {
    signature = await crypto.subtle.sign(
      { hash: "SHA-256", name: "ECDSA" },
      privateKey,
      new TextEncoder().encode(challenge.challenge),
    );
  } catch (error) {
    throw new BrowserTrustedDeviceError(
      "signingFailed",
      "The extractable key could not sign the challenge",
      { cause: error },
    );
  }

  return {
    challengeToken: challenge.challengeToken,
    installationId: installation.installationId,
    keyAlgorithm: "ES256",
    name: navigator.platform || "Web browser",
    platform: "web",
    publicKey: installation.publicKey,
    signature: bytesToBase64(signature),
    verificationMethod: "webCryptoExtractable",
  };
}
